import React, { useState, useEffect } from 'react';
import { get, map, omit, find, isEmpty, compact, orderBy, first, upperFirst } from 'lodash';
import { object, func, bool, string } from 'prop-types';
import { Modal, notification, Divider } from 'antd';
import moment from 'moment';
import { Text, FlexContainer, ConfirmFooterButtons, IconContainer, Icon, Row, If } from '../../common';
import { UPDATE_POLL, CREATE_POLL, DELETE_POLL, EXPORT_RESPONSES_POLL } from '../../../queries';
import { validateQuestions, formatActivationRange, formatStores, genUUID, typeQuestions } from '../../../utils/misc'

const styleNotification = {
    borderRadius: 8,
    height: 42,
    padding: '9px 12px',
    top: 45
};

notification.config({
    placement: 'topRight',
    duration: 3
});

const requiredFields = [
    { key: 'namePoll', translation: 'Nombre encuesta' },
    { key: 'title', translation: 'Título encuesta' },
    { key: 'welcomeMessage', translation: 'Mensaje de bienvenida' },
    { key: 'associatedStore', translation: 'Tienda' },
    { key: 'questions', translation: 'Preguntas' }
];

const showNotification = (isSuccess, text, textInPast, onClose) => {
    setTimeout(() => onClose(), 1000);

    const error = {
        msg: `Hubo un error al ${text} encuesta.`,
        color: '#BD2C00',
        icon: 'alert-circle',
        bgColor: '#F7E6E1',
        borderColor: '#BD2C00'
    };

    const success = {
        msg: `La encuesta se ${textInPast} correctamente.`,
        color: '#22A889',
        icon: 'checkmark-circle',
        bgColor: '#E6F5F1',
        borderColor: '#B6DFD6'
    };

    const { msg, bgColor, icon, color, borderColor } = isSuccess ? success : error;

    notification.open({
        message: (<Row align='center'>
            <Icon name={icon} size={24} color={color} />
            <Divider type='vertical' style={{ background: color, height: '1.5em', margin: '0px 16px 0px 14px' }} />
            <Text text={msg} size={12} color='#323A44' />
        </Row>),
        style: { ...styleNotification, backgroundColor: bgColor, border: `1px solid ${borderColor}` }
    });
};

const ModalConfirmation = ({ client, onClose, visible, pollSelected, action, refreshData, session }) => {
    const sessionSalesChannel = orderBy(map(get(session, 'salesChannels'), ({ name, store, id, kind }) => ({ label: `${store} - ${name}`, value: id, kind })), ['label'], ['asc']);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [missingFields, setMissingFields] = useState([]);
    const [validateRequiredFields, setValidateRequiredFields] = useState(false);
    const isDuplicate = action === 'duplicate';
    const isStatus = action === 'status';
    const isDelete = action === 'delete';
    const status = get(pollSelected, 'status');
    const idPoll = get(pollSelected, 'idPoll')

    const typesMessages = {
        duplicate: {
          nameIcon: 'duplicate',
          colorIcon: '#1fa02e',
          text: 'duplicar',
          textInPast: 'duplicó',
          action: () => handleSubmit()
        },
        delete: {
          nameIcon: 'caution',
          colorIcon: '#D60000',
          text: 'eliminar',
          textInPast: 'eliminó',
          action: () =>handleDelete()
        },
        export: {
          nameIcon: 'export',
          colorIcon: '#1fa02e',
          text: 'exportar',
          textInPast: 'exportó',
          action: () => handleExport()
        },
        status: {
            text: 'cambiar de estado',
            textInPast: 'editó',
            action: () => handleSubmit()
        }
    };

    useEffect(() => {
        if (!isEmpty(action) && isStatus && status === 2) {
            const filteredFields = compact(map(pollSelected, (value, PollFieldkey) => {
                return find(requiredFields, ({ key }) => {
                    return key === PollFieldkey && isEmpty(value);
                });
            }));

            if (!isEmpty(filteredFields)) {
                setValidateRequiredFields(true);
                setMissingFields(filteredFields);
            }
        }
    }, [action]);

    const addAuxId = (items, key) => map(items, (i) => ({ ...i, id: get(i, key)}))

    const addUUID = (questions) => {
        const questionsAndOptionsUpdated = map(addAuxId(questions, 'idQuestion'), (question) => {
            const questionUUID = genUUID()
            const options = map(addAuxId(get(question, 'options'), 'idOption'), (option) => {
                return { ...option, idFrontReference: genUUID() }
            })

            return { ...question, idFrontReference: questionUUID, options }
        })

        const questionsUpdated = map(questionsAndOptionsUpdated, (question) => {
            const idTypeQuestion = get(question, 'idTypeQuestion')
            const conditionsOptions = map(get(question, 'conditions'), (condition) => {
                const optionFound = find(get(question, 'options'), { id: get(condition, 'idOptions')})
                const nextQuestionFound = find(questionsAndOptionsUpdated, { id: get(condition, 'idNextQuestion')})

                return {
                    ...omit(condition, ['idCondition', 'idNextQuestion', 'idOptions', 'idQuestion']),
                    idFrontReferenceOption: idTypeQuestion === typeQuestions.CSAT ? '' : get(optionFound, 'idFrontReference'),
                    idFrontReferenceQuestion: get(nextQuestionFound, 'idFrontReference'),
                    idOptions: idTypeQuestion === typeQuestions.CSAT ? get(optionFound, 'idOption') : 0
                }
            })

            return {
                ...omit(question, ['id', 'idQuestion']),
                conditions: conditionsOptions,
                options: map(get(question, 'options'), (option) => omit(option, ['id', 'idOption', 'idQuestion']))
            }
        })


        return questionsUpdated
    }

    const handleSubmit = async () => {
        if (validateRequiredFields) return null

        const activationRange = first(get(pollSelected, 'activationRange'))

        const range = {
            init: moment(get(activationRange, 'init')),
            end: moment(get(activationRange, 'end'))
        }

        const stores = map(get(pollSelected, 'associatedStore'), ({ idStore }) => ({ value: idStore }))

        const formattedValues = {
            ...omit(pollSelected, ['dateCreate', 'dateUpdate', '__typename', 'idPoll', 'numberReponses']),
            questions: validateQuestions(addUUID(get(pollSelected, 'questions'))),
            associatedStore: formatStores(stores, {}, sessionSalesChannel),
            activationRange: formatActivationRange(range),
            monthsEvaluate: map(get(pollSelected, 'monthsEvaluate'), ({ months_evaluate }) => ({ months_evaluate })),
            status: 2
        };

        setLoading(true);
        const response = await client.mutate({
            mutation: isDuplicate ? CREATE_POLL : UPDATE_POLL,
            variables: {
                input: {
                    ...isDuplicate
                        ? formattedValues
                        : {
                            idPoll,
                            status: status === 1 ? 2 : 1,
                            userUpdate: get(session, 'email'),
                            changeStatus: true
                        }
                }
            }
        });

        const typeQuery = isDuplicate ? 'createPoll' : 'updatePoll';
        finishAction(typeQuery, response)
    };

    const finishAction = (typeQuery, response) => {
        const isSuccess = get(response, `data.${typeQuery}.internalCode`) === '200';
        const { text, textInPast } = typesMessages[action]

        setSuccess(isSuccess);
        showNotification(isSuccess, text, textInPast, onClose);
        setLoading(false);
        refreshData();
    }

    const handleDelete = async () => {
        setLoading(true);
        const response = await client.mutate({
            mutation: DELETE_POLL, variables: { input: { idPoll }}
        });

        finishAction('deletePoll', response)
    }

    const handleExport = async () => {
        setLoading(true);
        const response = await client.mutate({
            mutation: EXPORT_RESPONSES_POLL, variables: { input: { idPoll, email: get(session, 'email') }}
        });

        finishAction('exportReponses', response)
    }

    const renderMsgConfirmation = () => {
        const { nameIcon, colorIcon, text, } = typesMessages[action] || {}

        return (<>
            {!isStatus &&<IconContainer color='#ffffff' size={70}>
                <Icon name={nameIcon} color={colorIcon} size={46.8} />
            </IconContainer>}
            <Text text={`¿Seguro quiere ${text} esta encuesta?`} />
            <Text text={get(pollSelected, 'namePoll')} strong />
        </>)
    }

    return <Modal
        footer={null}
        title={`${upperFirst(get(typesMessages[action], 'text'))} encuesta`}
        visible={visible}
        onCancel={() => onClose()}
    >
        <FlexContainer style={{ marginBottom: 70 }} direction='column' align='center'>
            <If
                condition={validateRequiredFields}
                then={<>
                    <IconContainer color='#ffffff' size={70}>
                        <Icon name='caution' color='#D60000' size={46.8} />
                    </IconContainer>
                    <Text text={'Encuesta no puede ser activada sin campos requeridos.'} />
                    <Text text={get(pollSelected, 'namePoll')} strong />
                    {map(missingFields, ({ translation }) => {
                        return <Text text={`- ${translation}`} strong />;
                    })}
                </>}
                else={renderMsgConfirmation()}
            />
        </FlexContainer>

        <ConfirmFooterButtons
            loading={loading}
            justify='right'
            onCancel={() => onClose()}
            confirmLabel={isDelete ? 'Si, eliminar' : 'btn.action.confirm'}
            onConfirm={typesMessages[action] ? typesMessages[action].action : () => {}}
            confirmDisabled={success || validateRequiredFields}
            widthButtons={100}
        />
    </Modal>;
};

ModalConfirmation.propTypes = {
    client: object,
    onClose: func,
    visible: bool,
    pollSelected: object,
    action: string,
    refreshData: func,
    session: object
};

export default ModalConfirmation;
